import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import formatNumber, { defaultFilterDates, formatSoldQuantityLabel } from '../../utils/functions';

const PER_PAGE = 10;

const emptyStats = {
  products_count: 0,
  total_revenue: 0,
  total_qty: 0,
  top_format: null,
  top_category: null,
};

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const lineRevenue = (item) =>
  parseFloat(item.sold_quantity || 0) * parseFloat(item.variant_price || 0);

export default function BestSelling() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(emptyStats);
  const [formatsData, setFormatsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    period: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => ({
    period: '',
    ...defaultFilterDates(),
  }));
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [fromRow, setFromRow] = useState(0);
  const [toRow, setToRow] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'sold_quantity', direction: 'desc' });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/best-selling`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          per_page: PER_PAGE,
          search: appliedSearch || undefined,
          date_from: appliedFilters.dateFrom || undefined,
          date_to: appliedFilters.dateTo || undefined,
          period: appliedFilters.period || undefined,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
        },
      });

      const paginator = response.data?.data;
      setItems(Array.isArray(paginator?.data) ? paginator.data : []);
      setStats(response.data?.stats ?? emptyStats);
      setFormatsData(Array.isArray(response.data?.formats) ? response.data.formats : []);
      setCategoriesData(Array.isArray(response.data?.categories) ? response.data.categories : []);
      setLastPage(paginator?.last_page ?? 1);
      setTotalRows(paginator?.total ?? 0);
      setFromRow(paginator?.from ?? 0);
      setToRow(paginator?.to ?? 0);
    } catch (error) {
      console.error('Erreur lors de la récupération des ventes :', error);
      setItems([]);
      setStats(emptyStats);
      setFormatsData([]);
      setCategoriesData([]);
      setLastPage(1);
      setTotalRows(0);
      setFromRow(0);
      setToRow(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedSearch, appliedFilters, sortConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters({
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
      period: draftFilters.period,
    });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setDraftFilters({ period: '', ...defaultFilterDates() });
    setAppliedFilters({ period: '', ...defaultFilterDates() });
    setSearchInput('');
    setAppliedSearch('');
    setCurrentPage(1);
    setSortConfig({ key: 'sold_quantity', direction: 'desc' });
  };

  const fetchAllForExport = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${APP_API_URL}/best-selling`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        page: 1,
        per_page: stats.products_count || totalRows || 1000,
        for_export: true,
        search: appliedSearch || undefined,
        date_from: appliedFilters.dateFrom || undefined,
        date_to: appliedFilters.dateTo || undefined,
        period: appliedFilters.period || undefined,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      },
    });
    return Array.isArray(response.data?.data?.data) ? response.data.data.data : items;
  };

  const exportRows = (rows) =>
    rows.map((item) => {
      const soldQty = parseFloat(item.sold_quantity || 0);
      return {
        Produit: item.product_name,
        Code: item.sku,
        Unité: item.unit_symbol || item.unit_name,
        'Quantité vendue': formatSoldQuantityLabel(soldQty, item),
        "Chiffre d'affaires": `${formatNumber(lineRevenue(item))} XOF`,
      };
    });

  const exportToPDF = async () => {
    const rows = exportRows(await fetchAllForExport());
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('Produits les plus vendus', 14, 16);
    doc.autoTable({
      head: [['Produit', 'Code', 'Unité', 'Quantité vendue', "Chiffre d'affaires"]],
      body: rows.map((r) => [r.Produit, r.Code, r.Unité, r['Quantité vendue'], r["Chiffre d'affaires"]]),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 8 },
    });
    doc.save(`Produits vendus ${APP_NAME}.pdf`);
  };

  const exportToExcel = async () => {
    const rows = exportRows(await fetchAllForExport());
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produits');
    XLSX.writeFile(wb, `Produits vendus ${APP_NAME}.xlsx`);
  };

  const handlePrint = async () => {
    const rows = exportRows(await fetchAllForExport());
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        (r) =>
          `<tr><td>${r.Produit}</td><td>${r.Code}</td><td>${r.Unité}</td><td>${r['Quantité vendue']}</td><td>${r["Chiffre d'affaires"]}</td></tr>`
      )
      .join('');
    printWindow.document.write(`
      <html><head><title>Produits vendus</title>
      <style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px;font-size:12px}</style>
      </head><body><h1>Produits les plus vendus</h1><table>
      <thead><tr><th>Produit</th><th>Code</th><th>Unité</th><th>Quantité</th><th>CA</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const renderAggTable = (title, rows) => (
    <section className="duo-panel mt-3">
      <div className="duo-toolbar">
        <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
          {title}
        </span>
      </div>
      <div className="duo-table-wrap">
        <table className="duo-table">
          <thead>
            <tr>
              <th>Libellé</th>
              <th>Quantité vendue</th>
              <th>Chiffre d&apos;affaires</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row, index) => (
                <tr key={`${title}-${index}`}>
                  <td className="fw-bold">{row.name}</td>
                  <td>{formatNumber(row.quantity)}</td>
                  <td>{formatNumber(row.revenue)} XOF</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="duo-empty-row">
                  Aucune donnée pour ce regroupement.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Analytique</p>
          <h1 className="duo-hero-title">Produits les plus vendus</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Classement des ventes par produit, format et catégorie — {PER_PAGE} produits par page.
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Produits (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.products_count}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">CA total XOF</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.total_revenue)}
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Qté totale vendue</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(stats.total_qty)}
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Top format</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '0.85rem' }}>
              {stats.top_format ?? '—'}
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Top catégorie</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '0.85rem' }}>
              {stats.top_category ?? '—'}
            </p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Détail par produit
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button
                type="button"
                title="Rafraîchir"
                onClick={() => {
                  setCurrentPage(1);
                  fetchData();
                }}
                className="duo-btn duo-btn--ghost duo-btn--icon"
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setIsOpen(!isOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher produit, code, format, catégorie..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date début"
                    value={draftFilters.dateFrom}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value, period: '' }))
                    }
                  />
                  <input
                    type="date"
                    className="duo-input"
                    title="Date fin"
                    value={draftFilters.dateTo}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value, period: '' }))
                    }
                  />
                  <select
                    className="duo-select"
                    value={draftFilters.period}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        period: e.target.value,
                        dateFrom: e.target.value ? '' : prev.dateFrom,
                        dateTo: e.target.value ? '' : prev.dateTo,
                      }))
                    }
                  >
                    <option value="">Période rapide</option>
                    <option value="today">Aujourd&apos;hui</option>
                    <option value="yesterday">Hier</option>
                    <option value="this_week">Cette semaine</option>
                    <option value="this_month">Ce mois</option>
                    <option value="this_year">Cette année</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('product_name')}>
                    Produit <SortIcon active={isSortActive('product_name')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('sku')}>
                    Code <SortIcon active={isSortActive('sku')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('sold_quantity')}>
                    Quantité vendue{' '}
                    <SortIcon active={isSortActive('sold_quantity')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('revenue')}>
                    Chiffre d&apos;affaires{' '}
                    <SortIcon active={isSortActive('revenue')} direction={sortConfig.direction} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, index) => {
                    const soldQty = parseFloat(item.sold_quantity || 0);
                    return (
                      <tr key={`${item.sku}-${index}`}>
                        <td className="fw-bold">{item.product_name}</td>
                        <td>
                          <span className="duo-badge-pill duo-badge-pill--info">{item.sku}</span>
                        </td>
                        <td>{formatSoldQuantityLabel(soldQty, item)}</td>
                        <td>{formatNumber(lineRevenue(item))} XOF</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="duo-empty-row">
                      Aucun produit correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="duo-footer-bar">
            <span>
              {totalRows > 0
                ? `${fromRow} – ${toRow} sur ${totalRows} produit${totalRows > 1 ? 's' : ''}`
                : 'Aucun produit à afficher'}
            </span>
            <ul className="duo-pagination">
              <li>
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  disabled={currentPage <= 1 || totalRows === 0}
                  onClick={() => handlePageChange('prev')}
                >
                  Précédent
                </button>
              </li>
              <li>
                <span className="duo-pagination__info px-2">
                  Page {totalRows > 0 ? currentPage : 0} / {lastPage}
                </span>
              </li>
              <li>
                <button
                  type="button"
                  className="duo-btn duo-btn--primary"
                  disabled={currentPage >= lastPage || totalRows === 0}
                  onClick={() => handlePageChange('next')}
                >
                  Suivant
                </button>
              </li>
            </ul>
          </div>
        </section>

        {renderAggTable('Formats les plus vendus', formatsData)}
        {renderAggTable('Catégories les plus vendues', categoriesData)}
      </div>
    </MainLayout>
  );
}
